import { Component, Vue, Watch } from 'vue-property-decorator';
import { Set } from '@/models/Set';
import { Order } from '@/models/Order';
import { AxiosError } from 'axios';
import { currency } from '@/support/String';
import ErrorHandler from '@/support/ErrorHandler';
import LoaderCard from '@/components/loader-card/LoaderCard';
import { Options } from '@/components/mi-dialog/MiDialog';
import { User } from '@/models/User';
import StyledBox, { StyledBoxOptions } from '@/support/StyledBoxType';
import { LanguageOptions, languageOptions } from '@/support/Languages';
import { Rpc } from '@/models/Rpc';
import { debounce } from 'lodash';

@Component<OrderOverview>({
  components: {
    LoaderCard,
  },
  filters: {
    currency(value: string) {
      return !value ? '' : currency(parseInt(value));
    },
  }
})
export default class OrderOverview extends Vue {
  public $pageTitle = 'Order overview';
  protected orderItems: SetProduct[] = [];
  protected totalPrice: number = 0;
  protected totalItems: number = 0;
  protected order: Order | null = null;

  protected introText: string = '';
  protected introTextDebounce = debounce(this.updateIntroText, 300);
  protected outroText: string = '';
  protected outroTextDebounce = debounce(this.updateOutroText, 300);
  protected originalText: string = '';

  protected selectedLanguage: string = 'original';


  protected currentOrderId: string = '';
  protected finishedOrderId: string = '';
  protected currentOrderStatus: string = '';

  protected selectedStyledBox: string = '';
  protected selectedStylist: User | null = null;

  protected languageOptions: LanguageOptions[] = languageOptions;
  protected selectedLanguageForTranslation: string = '';
  protected isLoadingTranslation: boolean = false;
  protected isTextTranslated: boolean = false;
  protected translatedText: string = '';

  protected stylists: User[] | null = null;

  protected showCompletedDialog: boolean = false;
  protected isLoading: boolean = true;

  protected error: any;

  protected sets: OrderSet[] = [{
    name: '',
    set_items: [],
  }]

  protected set: SetProduct[] = [];


  public mounted() {
    this.currentOrderId = this.$route.params.id;
    this.initialize();
  }

  protected initialize() {
    this.isLoading = true;
    this.listSets();
    this.fetchOriginalText();
  }

  protected getOrder() {
    new Order()
      .include(['user', 'profile'])
      .find(this.$route.params.id)
      .then((order: any) => {
        this.order = order;
        this.selectedStyledBox = ((this.order as Order).box_type as string) ? ((this.order as Order).box_type as string) : '';
        this.currentOrderStatus = order.status;

        this.fetchUsers();
        this.isLoading = false;
      })
      .catch((error: AxiosError) => {
        this.isLoading = false;
        ErrorHandler.network(error);
      })
  }

  protected listSets() {
    new Set()
      .order(this.currentOrderId)
      .include(['set-items', 'product'])
      .all()
      .then((response: any) => {
        this.sets = response;
        this.calculateTotal();
        this.calculateTotalItems();
        this.getOrder();
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected calculateTotal() {
    let calculatedItems: string[] = [];
    this.totalPrice = 0;
    this.sets.forEach((set: OrderSet) => {
      if (set.set_items) {
        set.set_items.forEach((setProduct: SetProduct) => {
          if (calculatedItems.indexOf((setProduct as any).product.id) === -1) {
            this.totalPrice += parseInt((setProduct as any).product.retail_price);
          }
          calculatedItems.push((setProduct as any).product.id);
        });
      }
    });
    return this.totalPrice;
  }

  protected calculateTotalItems() {
    let calculatedItems: string[] = [];
    this.totalItems = 0;
    this.sets.forEach((set: OrderSet) => {
      if (set.set_items) {
        set.set_items.forEach((setProduct: SetProduct) => {
          if (calculatedItems.indexOf((setProduct as any).product.id) === -1) {
            this.totalItems += 1;
          }
          calculatedItems.push((setProduct as any).product.id);
        });
      }
    });
    return this.totalItems;
  }

  protected fetchUsers() {
    new User()
      .filter({ 'roles': ['stylist', 'administrator'] })
      .limit(100)
      .sort('name', 'ASC')
      .all()
      .then((users: User[]) => {
        this.stylists = users;
        this.selectedStylist = users.find((user: User) => {
          return user.name === (this.order as Order).stylist_name;
        }) as User;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }

  protected fetchOriginalText() {
    new Rpc()
      .execute('box-request:generate-note', {
        order_id: this.currentOrderId,
        nl2br: false,
      })
      .then((response: any) => {
        this.originalText = response.note;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      });
  }


  protected updateIntroText() {
    const payload = {
      intro: this.introText,
    }
    new Order()
      .include('user')
      .order(this.currentOrderId)
      .update(payload)
      .then((order: Order) => {
        this.fetchOriginalText();
      })
      .catch((error: AxiosError) => {
        this.error = error.response;
      });
  }

  protected updateOutroText() {
    const payload = {
      outro: this.outroText,
    }
    new Order()
      .include('user')
      .order(this.currentOrderId)
      .update(payload)
      .then((order: Order) => {
        this.fetchOriginalText();
      })
      .catch((error: AxiosError) => {
        this.error = error.response;
      });
  }


  protected updateBox() {
    if (!(this.order as Order).id) {
      return;
    }

    const payloadUpdate = {
      box_type: this.selectedStyledBox,
      orders: [(this.order as Order).id],
      stylist: this.selectedStylist ? this.selectedStylist.id : null,
    }

    new Order()
      .include('user')
      .order((this.order as Order).id as string)
      .update(payloadUpdate)
      .then((order: Order) => {
        if (!order || !order.user) {
          return;
        }
        this.introText = order.intro as string;
        this.outroText = order.outro as string;

      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      })
  }

  protected finishOrder() {
    this.isLoading = true;
    this.completeOrder();
  }

  protected completeOrder() {

    let payload: FinalizePayload = {
      intro: this.introText,
      outro: this.outroText,
    };

    if (this.selectedLanguage === 'translated') {
      payload = {
        ...payload,
        translated_language: this.selectedLanguageForTranslation,
        translated_note: this.translatedText,
      };
    }

    new Order()
      .finalizeOrder(this.currentOrderId, payload)
      .then((order: Order) => {
        this.showCompletedDialog = true;
        this.isLoading = false;
      })
      .catch((error: AxiosError) => {
        this.error = error.response;
        this.$store.dispatch('openDialog', this.dialogMissingAddressOptions);
        this.isLoading = false;
      });
  }

  protected translate() {
    this.isLoadingTranslation = true;
    new Rpc()
      .execute('box-request:translate-note', {
        "target_language": this.selectedLanguageForTranslation,
        "order_id": this.currentOrderId,
      })
      .then((response: any) => {
        this.translatedText = response.translated;
      })
      .catch((error: AxiosError) => {
        ErrorHandler.network(error);
      })
      .finally(() => {
        this.isTextTranslated = true;
        this.isLoadingTranslation = false;
      })
  }


  protected onInput(value: string | null | undefined) {
    if (value === undefined || null) {
      this.reset();
    }
  }

  protected reset() {
    this.selectedLanguageForTranslation = '';
    this.isTextTranslated = false;
  }

  get dialogMissingAddressOptions(): Options {
    return {
      title: this.error.data.message ? this.error.data.message : this.error.data.errors.message,
      text: this.error.data.errors.user ? this.error.data.errors.user[0] : this.error.data.errors.field,
      type: 'info',
      buttons: {
        confirm: {
          text: 'Ga terug naar order detail',
          action: () => {
            if (!this.order || !this.order.user) return;
            const userType = this.order.user_type === 'contact' ? 'contacten' : 'leveranciers';
            if (!this.order.user.profile) {
              return;
            }
            const userId = this.order.user_type === 'contact' ? this.order.user.id : this.order.user.profile.supplier_id;
            //this.$router.push(`/${userType}/${userId}?finishOrder=${this.order.id}`);
            // GET finishOrder is used by user.ts to decide whether or not user can finish order.
            // user is not allowed to finish order...
            this.$router.push(`/${userType}/${userId}?currentOrderId=${this.order.id}`);
          }
        },
        cancel: {
          text: this.$t('dialogOptions.button.close').toString(),
          action: () => {
            this.error = null;
          },
        },
      },
    };
  }

  get dialogOptions(): Options {
    return {
      title: 'Order update',
      text: 'Order is nu in preview',
      type: 'success',
      buttons: {
        confirm: {
          text: 'Oke',
          action: () => {
            this.initialize();
          }
        },
      },
    };
  }

  public get styledBoxOptions(): StyledBoxOptions[] {
    return StyledBox.styledBoxOptions;
  }


  @Watch('selectedStyledBox')
  @Watch('selectedStylist')
  protected styledBoxChanged() {
    this.updateBox();
  }
}

interface SetProduct {
  name: string;
  brand: number;
  color: string;
  id: string;
  image_url: string;
  retail_price: string;
  size: string;
  supplier: string;
}

interface OrderSet {
  name: string;
  set_items: SetProduct[];
}

interface FinalizePayload {
  intro: string;
  outro: string;
  translated_language?: string;
  translated_note?: string;
}
